/**
 * Legenda colori del Centro Ticket V3.
 * Fonte: SPEC-implementazione.md §4 (prototipo L502-L505 per i 3 stati e le 3
 * priorita mostrate), estesa ai 6 stati e alle 4 priorita reali (ruling 3).
 */
import { ORIGIN_LABELS, PRIORITY_LABELS, REQUESTER_TYPE_LABELS, STATUS_LABELS } from '../constants';
import { formatDuration } from '../../../utils/formatDuration';
import type { SlaInfo, SlaLevel } from '../../../utils/supportSla';

export type S3Tone = 'green' | 'warn' | 'dang' | 'chip' | 'info' | 'vio' | 'cyan';

export interface S3Visual { tone: S3Tone; label: string }

const STATUS_TONES: Record<string, S3Tone> = {
  aperto: 'green',
  preso_in_carico: 'green',
  in_lavorazione: 'green',
  in_attesa_associato: 'warn',
  risolto: 'chip',
  chiuso: 'chip',
};

export const statusVisual = (status: string): S3Visual => ({
  tone: STATUS_TONES[status] || 'chip',
  label: STATUS_LABELS[status] || status,
});

const PRIORITY_TONES: Record<string, S3Tone> = {
  bassa: 'chip', media: 'warn', alta: 'dang', urgente: 'dang',
};

export const priorityVisual = (priority: string): S3Visual => ({
  tone: PRIORITY_TONES[priority] || 'chip',
  label: PRIORITY_LABELS[priority] || priority,
});

/**
 * Barra 4px a sinistra della riga di coda (L142, PB[..][2]).
 * I quattro livelli devono restare visivamente distinti in ENTRAMBI i temi:
 * `urgente` usa il rosso pieno (ruling 3), `alta` il rosso attenuato
 * `--a2-dang-soft` (in chiaro `--a2-dang-fg` coincide con `--a2-danger`).
 */
const PRIORITY_RAILS: Record<string, string> = {
  bassa: 'var(--a2-inbd)',
  media: 'var(--a2-warning)',
  alta: 'var(--a2-dang-soft)',
  urgente: 'var(--a2-danger)',
};

export const priorityRailColor = (priority: string): string =>
  PRIORITY_RAILS[priority] || 'var(--a2-inbd)';

const SLA_TONES: Record<string, S3Tone> = {
  ok: 'green', warning: 'warn', breach: 'dang', paused: 'chip',
};

export const slaVisual = (level: SlaLevel): S3Tone => SLA_TONES[level] || 'chip';

export interface S3SlaBadge {
  tone: S3Tone;
  label: string;
  title: string;
  /** Livello e scadenza di origine: servono alla nota SLA della tabella (facoltativi). */
  level?: SlaLevel;
  target?: SlaInfo['target'];
}

/**
 * Badge SLA in versione V3. Riproduce esattamente le etichette di
 * `SlaChip.tsx:18-27`, rimosso col Task 13 quando l'ultima pagina che lo
 * montava e passata alla V3 (la citazione resta come provenienza, leggibile
 * nella storia git), ma restituisce dati invece di markup: cosi e testabile e
 * riusabile sia nella riga di coda sia nell'header del ticket.
 */
export const slaBadge = (info: SlaInfo | null): S3SlaBadge | null => {
  if (!info) return null;
  const target = info.target === 'firstResponse' ? 'risposta' : 'risoluzione';
  const label =
    info.level === 'paused'
      ? 'SLA in pausa'
      : info.level === 'breach'
        ? `SLA ${target} sforato da ${formatDuration(-info.remainingMs)}`
        : `SLA ${target}: ${formatDuration(info.remainingMs)}`;
  return {
    tone: slaVisual(info.level),
    label,
    title: `Scadenza ${target}: ${info.dueAt.toLocaleString('it-IT')}`,
    level: info.level,
    target: info.target,
  };
};

export type SlaNoteKind = 'breach' | 'warning' | 'paused';

/**
 * Nota SLA sotto lo stato, nella coda a tabella (niente piu colonna SLA): scritta
 * leggibile, non maiuscola, solo quando c'e da guardare. Sforato = «… in
 * ritardo» (rosso), vicino alla scadenza = «… in scadenza» (ambra, il chip
 * giallo di prima), in pausa = «SLA in pausa» (grigio). SLA rispettato: nessuna
 * nota. Il tempo e la scadenza restano nel tooltip (`slaQueueTitle`).
 */
export const slaQueueNote = (badge: S3SlaBadge | null): { kind: SlaNoteKind; text: string } | null => {
  if (!badge || !badge.level) return null;
  const what = badge.target === 'firstResponse' ? 'Risposta' : 'Risoluzione';
  if (badge.level === 'breach') return { kind: 'breach', text: `${what} in ritardo` };
  if (badge.level === 'warning') return { kind: 'warning', text: `${what} in scadenza` };
  if (badge.level === 'paused') return { kind: 'paused', text: 'SLA in pausa' };
  return null;
};

/** Tooltip della cella dello stato: l'etichetta del chip di prima (col tempo) e la scadenza. */
export const slaQueueTitle = (badge: S3SlaBadge | null): string | undefined =>
  badge ? `${badge.label} — ${badge.title}` : undefined;

export const originVisual = (origin?: string): S3Visual =>
  origin ? { tone: 'info', label: ORIGIN_LABELS[origin] || origin } : { tone: 'chip', label: '' };

const REQUESTER_TONES: Record<string, S3Tone> = {
  associate: 'green', guest: 'vio', user: 'chip',
};

export const requesterTypeVisual = (type?: string): S3Visual => ({
  tone: (type && REQUESTER_TONES[type]) || 'chip',
  label: (type && REQUESTER_TYPE_LABELS[type]) || '',
});

/**
 * Glifo di stato del redesign v4 (N11, `Centro Ticket v4.dc.html` L808-L815):
 * Aperto vuoto, Preso in carico mezzo pieno, In lavorazione pieno, In attesa
 * ambra, Risolto grigio pieno, Chiuso grigio chiaro. Le etichette restano
 * quelle operatore di `STATUS_LABELS` (rischio R-11), mai quelle del prototipo.
 * Aggiunta pura: le funzioni sopra non cambiano.
 */
export type S3GlyphKind = 'open' | 'taken' | 'working' | 'waiting' | 'resolved' | 'closed';

const STATUS_GLYPHS: Record<string, S3GlyphKind> = {
  aperto: 'open',
  preso_in_carico: 'taken',
  in_lavorazione: 'working',
  in_attesa_associato: 'waiting',
  risolto: 'resolved',
  chiuso: 'closed',
};

export const statusGlyph = (status: string): { kind: S3GlyphKind; label: string } => ({
  kind: STATUS_GLYPHS[status] || 'open',
  label: STATUS_LABELS[status] || status,
});
